// Executes structured assistant actions against the live site. This is the only
// place that touches the router/DOM, so the text and voice providers share
// identical behaviour.

import type { AssistantAction } from './types'
import { requestSkipAutoScroll } from './scrollControl'
import { resolveAndHighlight } from './targetResolver'

type NavigateFn = (path: string) => void

/**
 * Repeatedly try to resolve+highlight `query` until it succeeds or `timeout`
 * elapses — handles a freshly navigated (lazy-loaded) page still mounting.
 */
function highlightWhenReady(query: string, timeout: number) {
  const deadline = Date.now() + timeout
  const tick = () => {
    if (resolveAndHighlight(query)) return
    if (Date.now() < deadline) window.setTimeout(tick, 90)
  }
  tick()
}

export interface ExecuteOptions {
  navigate: NavigateFn
  currentPath: string
}

/** Run a single assistant action. Safe to call from any turn. */
export function executeAction(action: AssistantAction, opts: ExecuteOptions): void {
  switch (action.type) {
    case 'navigate': {
      if (action.path !== opts.currentPath) opts.navigate(action.path)
      else window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    case 'open-tab': {
      window.open(action.url, '_blank', 'noopener,noreferrer')
      return
    }

    case 'highlight': {
      if (action.path !== opts.currentPath) {
        // Tell ScrollToTop to stand down, navigate, then resolve once mounted.
        requestSkipAutoScroll()
        opts.navigate(action.path)
        window.setTimeout(() => highlightWhenReady(action.query, 6000), 150)
      } else {
        highlightWhenReady(action.query, 2500)
      }
      return
    }
  }
}
